import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Building2, Users, DollarSign, TrendingUp, Activity, 
  ArrowUpRight, ArrowDownRight, BarChart3, Zap, Clock 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLAN_COLORS = {
  // Current subscription plans (source of truth: /api/plans/public)
  launch: '#3b82f6',
  growth: '#8b5cf6',
  scale: '#f59e0b',
  demo: '#10b981',
  // Legacy plan slugs — kept so historical tenants render with a colored badge.
  free: '#94a3b8',
  starter: '#3b82f6',
  pro: '#8b5cf6',
  enterprise: '#f59e0b',
};
const FALLBACK_COLOR = '#64748b';

export default function SuperAdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [history, setHistory] = useState([]); // 30-day trend series
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem('superAdminToken');
      const hdr = { headers: { Authorization: `Bearer ${token}` } };
      try {
        // Fire both in parallel — both are cheap and cached on the backend
        const [a, h] = await Promise.all([
          axios.get(`${API}/platform/analytics`, hdr),
          axios.get(`${API}/platform/analytics/history?days=30`, hdr).catch(() => null),
        ]);
        setAnalytics(a.data);
        setHistory(h?.data?.series || []);
      } catch (error) {
        toast.error('Failed to fetch analytics');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Plan order preference for legend (matches subscription tier order)
  const PLAN_ORDER = ['launch', 'growth', 'scale', 'demo', 'free', 'starter', 'pro', 'enterprise'];
  const formatPlanName = (slug) => (slug || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const planData = analytics?.tenants_by_plan ?
    Object.entries(analytics.tenants_by_plan)
      // Drop empty plans so the chart isn't dominated by zero-value slices
      .filter(([, value]) => Number(value) > 0)
      .map(([name, value]) => ({
        slug: name,
        name: formatPlanName(name),
        value,
        color: PLAN_COLORS[name] || FALLBACK_COLOR,
      }))
      .sort((a, b) => PLAN_ORDER.indexOf(a.slug) - PLAN_ORDER.indexOf(b.slug))
    : [];

  const formatINR = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN');

  return (
    <div className="space-y-8" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div>
        <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Platform Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Overview of your SaaS platform performance</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Tenants"
          value={analytics?.total_tenants || 0}
          icon={Building2}
          trend={`${analytics?.active_tenants || 0} active`}
          color="indigo"
        />
        <MetricCard
          title="Total Users"
          value={analytics?.total_users || 0}
          icon={Users}
          trend="Across all tenants"
          color="emerald"
        />
        <MetricCard
          title="Monthly Revenue"
          value={formatINR(analytics?.revenue_monthly)}
          icon={DollarSign}
          trend="MRR (est.)"
          color="amber"
        />
        <MetricCard
          title="Active Rate"
          value={analytics?.total_tenants ? `${Math.round((analytics.active_tenants / analytics.total_tenants) * 100)}%` : '0%'}
          icon={Activity}
          trend={`${analytics?.suspended_tenants || 0} suspended`}
          color="rose"
        />
      </div>

      {/* 30-Day Trends — sparklines computed from platform_dashboard_history.
          Series is forward-filled, so flat lines on Day 0/1 are expected until
          enough days have accumulated. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <TrendCard
          title="Tenants — 30d"
          data={history}
          field="total_tenants"
          color="#6366f1"
          formatter={v => v}
          data-testid="trend-tenants"
        />
        <TrendCard
          title="Users — 30d"
          data={history}
          field="total_users"
          color="#10b981"
          formatter={v => v}
          data-testid="trend-users"
        />
        <TrendCard
          title="MRR — 30d"
          data={history}
          field="revenue_monthly"
          color="#f59e0b"
          formatter={v => formatINR(v)}
          data-testid="trend-mrr"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-indigo-400" />
              Tenants by Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {planData.length > 0 ? (
              <div className="flex items-center justify-center">
                <div className="w-64 h-64" style={{ minWidth: 256, minHeight: 256 }}>
                  <ResponsiveContainer width={256} height={256}>
                    <PieChart>
                      <Pie
                        data={planData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {planData.map((entry, index) => (
                          <Cell key={`cell-${entry.name || index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: '#1e293b', 
                          border: '1px solid #334155',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-3 ml-8">
                  {planData.map((plan) => (
                    <div key={plan.name} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: plan.color }} />
                      <span className="text-slate-300 text-sm">{plan.name}</span>
                      <Badge variant="outline" className="text-slate-400 border-slate-600">
                        {plan.value}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center text-slate-500 py-8">No tenant data yet</div>
            )}
          </CardContent>
        </Card>

        {/* Recent Signups */}
        <Card className="bg-slate-900 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="h-5 w-5 text-amber-400" />
              Recent Signups
            </CardTitle>
          </CardHeader>
          <CardContent>
            {analytics?.recent_signups?.length > 0 ? (
              <div className="space-y-3">
                {analytics.recent_signups.slice(0, 5).map((tenant, idx) => (
                  <div key={`supe-${idx}`} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg border border-slate-700">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-600/20 rounded">
                        <Building2 className="h-4 w-4 text-indigo-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{tenant.name}</p>
                        <p className="text-slate-500 text-xs">{new Date(tenant.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Badge 
                      style={{ 
                        backgroundColor: (PLAN_COLORS[tenant.plan] || FALLBACK_COLOR) + '20',
                        color: PLAN_COLORS[tenant.plan] || FALLBACK_COLOR,
                        border: `1px solid ${(PLAN_COLORS[tenant.plan] || FALLBACK_COLOR)}40`
                      }}
                    >
                      {(tenant.plan || '').replace(/-/g, ' ')}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-slate-500 py-8">No signups yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card className="bg-slate-900 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => window.location.href = '/super-admin/tenants'}
            data-testid="quick-action-tenants"
          >
            <Building2 className="mr-2 h-4 w-4" />
            Manage Tenants
          </Button>
          <Button 
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            onClick={() => window.location.href = '/super-admin/payments'}
            data-testid="quick-action-payments"
          >
            <DollarSign className="mr-2 h-4 w-4" />
            View Payments
          </Button>
          <Button 
            variant="outline"
            className="border-slate-600 text-slate-300 hover:bg-slate-800"
            onClick={() => window.location.href = '/super-admin/analytics'}
            data-testid="quick-action-analytics"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Full Analytics
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Compact sparkline card with current value, % delta vs 30 days ago, and a
// recharts line. Designed to slot into a 3-column grid below the metric row.
function TrendCard({ title, data, field, color, formatter }) {
  const safe = Array.isArray(data) ? data : [];
  const first = safe.length ? Number(safe[0][field] || 0) : 0;
  const last = safe.length ? Number(safe[safe.length - 1][field] || 0) : 0;
  const delta = last - first;
  const pct = first > 0 ? Math.round((delta / first) * 100) : (last > 0 ? 100 : 0);
  const deltaUp = delta >= 0;
  const deltaColor = deltaUp ? '#10b981' : '#ef4444';
  const sign = deltaUp ? '+' : '';
  const fmt = formatter || (v => v);

  return (
    <Card className="bg-slate-900 border-slate-700 hover:border-slate-600 transition-colors">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{fmt(last)}</p>
          </div>
          <div
            className="text-xs font-semibold px-2 py-1 rounded"
            style={{ color: deltaColor, backgroundColor: deltaColor + '20' }}
          >
            {sign}{pct}%
          </div>
        </div>
        <div style={{ height: 56 }}>
          {safe.length > 1 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={safe} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
                <Line
                  type="monotone"
                  dataKey={field}
                  stroke={color}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value) => [fmt(value), title.replace(/\s*\u2014\s*30d/i, '')]}
                  labelFormatter={(label, payload) => payload?.[0]?.payload?.date || label}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-slate-600 text-xs">
              Collecting history… check back tomorrow
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function MetricCard({ title, value, icon: Icon, trend, color }) {
  const colorClasses = {
    indigo: 'bg-indigo-600/20 text-indigo-400',
    emerald: 'bg-emerald-600/20 text-emerald-400',
    amber: 'bg-amber-600/20 text-amber-400',
    rose: 'bg-rose-600/20 text-rose-400'
  };

  return (
    <Card className="bg-slate-900 border-slate-700 hover:border-slate-600 transition-colors">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-slate-400 text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold text-white mt-2">{value}</p>
            <p className="text-slate-500 text-xs mt-2">{trend}</p>
          </div>
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Skeleton placeholder: renders the full dashboard shell instantly so the page
// feels responsive while analytics data is fetched. Once analytics arrives, the
// real layout replaces this in-place with no jump.
function DashboardSkeleton() {
  return (
    <div className="space-y-8 animate-pulse" style={{ fontFamily: 'DM Sans, sans-serif' }} data-testid="superadmin-dashboard-skeleton">
      <div>
        <div className="h-8 w-72 bg-slate-800 rounded" />
        <div className="h-4 w-96 bg-slate-800/70 rounded mt-3" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map(i => (
          <Card key={i} className="bg-slate-900 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-3 w-24 bg-slate-800 rounded" />
                  <div className="h-8 w-20 bg-slate-800 rounded mt-3" />
                  <div className="h-3 w-16 bg-slate-800/70 rounded mt-3" />
                </div>
                <div className="h-12 w-12 bg-slate-800 rounded-lg" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[0, 1].map(i => (
          <Card key={i} className="bg-slate-900 border-slate-700">
            <CardContent className="p-6">
              <div className="h-4 w-32 bg-slate-800 rounded" />
              <div className="h-48 w-full bg-slate-800/60 rounded-lg mt-4" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
